import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { ChargeType, Job } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { useClientStore } from '@core/client/useClientStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useJobUtils } from '@/modules/jobs/hooks/useJobUtils';
import { formatChargeTypeLabel } from '@/utils/client.constants';
import {
  actionLinkClassName,
  actionPrimaryButtonClassName,
} from '@core/theme/actionTheme.util';

interface RateSectionProps {
  job: Job;
  onJobUpdated?: (job: Job) => void;
  layout?: 'drawer' | 'page';
}

const RateSection = ({
  job,
  onJobUpdated,
  layout = 'drawer',
}: RateSectionProps) => {
  const { formatEstimatedCost } = useJobUtils();
  const { updateJob, fetchJobById } = useJobStore();
  const { getClientById } = useClientStore();
  const { getTerm } = useTemplateStore();

  const client = job.clientId ? getClientById(job.clientId) : null;
  const clientChargeType = client?.chargeType;
  const hideJobMarginEdit =
    clientChargeType === ChargeType.Fixed ||
    clientChargeType === ChargeType.RateCard;
  // Get client rate for display
  const clientRateCents =
    job.finalClientPayRateCents ??
    job.potentialPayRates?.potentialClientPayRateCeilingInCents;

  // Margin is directly available from job
  const displayMargin = job.marginPercentage
    ? job.marginPercentage * 100
    : null;

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [marginInput, setMarginInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Sync input with display margin when job changes
  useEffect(() => {
    setMarginInput(displayMargin?.toString() ?? '');
  }, [displayMargin]);

  const clientChargeRate = formatEstimatedCost(clientRateCents);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateJob(job.id, {
        marginPercentage: marginInput ? parseFloat(marginInput) / 100 : null,
      });
      const updatedJob = await fetchJobById(job.id);
      if (updatedJob) {
        onJobUpdated?.(updatedJob);
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating margin:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setMarginInput(displayMargin?.toString() ?? '');
    setIsEditing(false);
  };

  const renderClientChargeRate = () => {
    if (!clientChargeRate) return 'Not set';
    if (hideJobMarginEdit) return clientChargeRate;
    return `Est. ${clientChargeRate}`;
  };

  const isPageLayout = layout === 'page';

  const content = (
    <>
      <div
        className={`flex items-center justify-between ${isPageLayout ? 'mb-4' : 'mb-3'}`}
      >
        {isPageLayout ? (
          <h2 className="text-xl font-semibold">Rate</h2>
        ) : (
          <h3 className="text-sm font-semibold text-gray-900">Rate</h3>
        )}
        {!isEditing && !hideJobMarginEdit && (
          <button
            onClick={() => setIsEditing(true)}
            className={`flex items-center gap-1 text-sm font-medium ${actionLinkClassName}`}
          >
            <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
            Edit
          </button>
        )}
      </div>
      <dl
        className={
          isPageLayout ? 'space-y-4' : 'space-y-2 rounded-md bg-white p-2'
        }
      >
        {!hideJobMarginEdit && (
          <div className="flex justify-between">
            <dt className="text-sm font-medium text-gray-500">Job margin</dt>
            <dd className="text-sm text-gray-900">
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={marginInput}
                    onChange={(e) => setMarginInput(e.target.value)}
                    className="w-16 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                    placeholder="0"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <span>%</span>
                </div>
              ) : displayMargin !== null ? (
                `${Number.isInteger(displayMargin) ? displayMargin : displayMargin.toFixed(1)}%`
              ) : (
                'Not set'
              )}
            </dd>
          </div>
        )}
        <div className="flex justify-between">
          <dt className="text-sm font-medium text-gray-500">
            Client charge type
          </dt>
          <dd className="text-sm text-gray-900">
            {clientChargeType
              ? formatChargeTypeLabel(clientChargeType)
              : 'Not set'}
          </dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-sm font-medium text-gray-500">
            {getTerm(TemplateTerm.CLIENT, false)} charge rate
          </dt>
          <dd className="text-sm text-gray-900">{renderClientChargeRate()}</dd>
        </div>
      </dl>
      {isEditing && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${actionPrimaryButtonClassName}`}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}
    </>
  );

  if (isPageLayout) {
    return <div className="rounded-lg bg-white p-6 shadow-sm">{content}</div>;
  }

  return <div>{content}</div>;
};

export default RateSection;
